import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuthContext } from './AuthContext';
// Removed unused Ticket import
import { playNotificationSound, initializeAudio, setupAudioContextEvents } from '../services/soundService';
import { useTicketRefresh } from './TicketRefreshContext';
import config from '../config';
import { notificationService } from '../services/notificationService';

interface OmnichannelEvent {
  type:
    | 'new_message'
    | 'new_conversation'
    | 'conversation_updated'
    | 'message_status_updated'
    | 'typing_indicator'
    | 'tags_updated'
    | 'form_submitted';
  data: {
    conversation_id?: string;
    message_id?: string;
    conversation?: any;
    message?: any;
    user_id?: number;
    user_name?: string;
    is_typing?: boolean;
    tags?: string[];
    form_submission?: Record<string, unknown>;
    submitted_at?: string;
    status?: 'read' | 'pending' | 'sent' | 'delivered' | 'failed';
  };
  timestamp: string;
}

interface NotificationContextType {
  unreadCount: number;
  notifications: Notification[];
  markAsRead: (notificationId: string | number) => void;
  markAllAsRead: () => void;
  soundEnabled: boolean;
  toggleSound: () => Promise<void>;
  connectionStatus: 'connected' | 'disconnected' | 'error';
  initializeAudio: () => Promise<boolean>;
  loadMoreNotifications: () => Promise<void>;
  hasMoreNotifications: boolean;
  subscribeToOmnichannel: (callback: (event: OmnichannelEvent) => void) => () => void;
}

type NotificationType = 'new' | 'update' | 'statusChange' | 'comment' | 'bulk_update';

// Define a simplified ticket type for notifications
interface NotificationTicket {
  id: number;
  title: string;
  status: string;
  // Add minimal required properties
  description?: string;
  priority?: string;
  'customer.full_name'?: string;
  is_public?: boolean;
}

interface Notification {
  id: string | number; // Can be string for client-generated or number from database
  type: NotificationType;
  ticket: NotificationTicket;
  read: boolean;
  createdAt: Date;
  message: string;
  // Flag to identify if this notification came from WebSocket or API
  fromWebSocket?: boolean;
}

// Message formats are now directly handled in the WebSocket event handler
// and in the backend controllers

// Get sound preference from localStorage
const getSoundPreference = (): boolean => {
  const pref = localStorage.getItem('notificationSound');
  return pref === null ? true : pref === 'true';
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(getSoundPreference);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const omnichannelSubscribersRef = useRef<Set<(event: OmnichannelEvent) => void>>(new Set());
  // Initialize unread count from localStorage if available
  const [unreadCount, setUnreadCount] = useState(() => {
    const savedCount = localStorage.getItem('unreadNotificationCount');
    return savedCount ? parseInt(savedCount, 10) : 0;
  });
  const { user } = useAuthContext();
  const { triggerRefresh } = useTicketRefresh();
  


  // Load WebSocket notifications from localStorage on component mount
  useEffect(() => {
    try {
      const storedNotificationsStr = localStorage.getItem('webSocketNotifications');
      if (storedNotificationsStr) {
        const storedNotifications = JSON.parse(storedNotificationsStr);

        
        // Convert stored notifications to the proper format and ensure they all have ticket IDs
        const formattedNotifications: Notification[] = storedNotifications
          .filter((n: any) => n.ticketId) // Filter out notifications without ticket ID
          .map((n: any) => ({
            id: n.id,
            type: n.type as NotificationType,
            ticket: {
              id: n.ticketId,
              title: n.ticketTitle || n.message || `Ticket #${n.ticketId}`,
              status: n.ticketStatus || 'unknown',
              description: n.ticketDescription || '',
              priority: n.ticketPriority || 'normal',
              'customer.full_name': n.customerName || '',
              is_public: n.isPublic || false
            },
            read: false,
            createdAt: new Date(n.timestamp || Date.now()),
            message: n.message || `Update to ticket #${n.ticketId}`,
            fromWebSocket: true
          }));
        
        // Add stored notifications to state
        if (formattedNotifications.length > 0) {
          setNotifications(prev => {
            // Filter out any duplicates
            const existingIds = new Set(prev.map(n => n.id.toString()));
            const uniqueNotifications = formattedNotifications.filter(n => !existingIds.has(n.id.toString()));
            

            return [...uniqueNotifications, ...prev];
          });
          
          // Only count unread notifications from localStorage
          const unreadLocalNotifications = formattedNotifications.filter(n => !n.read).length;
          setUnreadCount(prev => prev + unreadLocalNotifications);
        }
      }
    } catch (error) {

    }
  }, []);

  const fetchNotifications = useCallback(async (page = 1) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    if (!user) {
      return;
    }

    if (loading) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await notificationService.getNotifications({
        page,
        limit: 20,
        read: false
      });

      const data = response;
      // Filter out notifications without valid ticket information
      // Filter out notifications without valid ticket information and ensure each notification has a ticket object
      const apiNotifications = data.notifications
        /* removed filter in this line because bulk noti will return ticket id 0 and it filtered out.*/
        .map((n: any) => ({
          ...n,
          // Ensure ticket object exists with at least an ID
          ticket: n.ticket || { 
            id: n.ticket_id,
            title: n.ticket_title || 'Unknown Ticket',
            description: (n.is_bulk ? `Related Tickets: ${n.related_ticket_ids.join(', ')}` : ``),
            status: n.ticket_status || 'unknown'
          },
          createdAt: new Date(n.createdAt || n.created_at),
          // Ensure message exists
          message: n.message || `Update to ticket #${n.ticket_id}`
        }));



      if (page === 1) {
        setNotifications(apiNotifications);
      } else {
        setNotifications(prev => [...prev, ...apiNotifications]);
      }
      
      // Calculate unread count from notifications
      const apiUnreadCount = data.notifications.filter((n: any) => !n.read).length;
      
      // Only update the unread count if we have actual data from the API
      if (data.notifications.length > 0) {
        setUnreadCount(apiUnreadCount);
      }
      setHasMoreNotifications(page < data.totalPages);
      setCurrentPage(page);
      setLoading(false);
    } catch (error) {
      // Handle fetch error
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load more notifications
  const loadMoreNotifications = async () => {
    if (hasMoreNotifications && !loading) {
      await fetchNotifications(currentPage + 1);
    }
  };

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string | number) => {
    if (!user) return;

    try {
      await notificationService.markAsRead(notificationId);
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {

    }
  }, [user]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      await notificationService.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      setUnreadCount(0);
      fetchNotifications();
    } catch (error) {

    }
  }, [user, fetchNotifications]);

  // Update localStorage whenever unreadCount changes
  useEffect(() => {
    localStorage.setItem('unreadNotificationCount', unreadCount.toString());
  }, [unreadCount]);

  // Effect to fetch notifications when user is authenticated
  useEffect(() => {
    let mounted = true;
    const loadNotifications = async () => {
      if (!user) {
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Load notifications for current user
      
      if (mounted && !loading) {
        try {
          await fetchNotifications();
          // Successfully loaded initial notifications
        } catch (error) {
          // Handle error loading initial notifications
        }
      }
    };

    loadNotifications();

    return () => {
      mounted = false;
    };
    
  }, [user, fetchNotifications]); // Re-run when user changes

  useEffect(() => {
    if (!user) {
      setConnectionStatus('disconnected');
      return;
    }

    // Connect to WebSocket server
    
    // Connect to WebSocket server using configured URL
    const socket = io(config.WS_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      reconnectionAttempts: 5, // Try to reconnect 5 times
      reconnectionDelay: 1000, // Wait 1 second between attempts
      timeout: 10000, // Connection timeout after 10 seconds
      auth: {
        token: localStorage.getItem('token'), // Send auth token if you have one
      },
      query: {
        tenantId : localStorage.getItem('tenantId')
      }
    });
    
    // Socket.io instance created

    // Log connection status for debugging
    socket.on('connect', () => {
      setConnectionStatus('connected');

      // Authenticate user
      socket.emit('authenticate', {user, tenantId: localStorage.getItem('tenantId')});
    });

    socket.on('connect_error', () => {
      setConnectionStatus('error');
    });

    socket.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });
    
    // Add event listeners for socket events
    socket.on('error', () => {
      // Handle WebSocket errors
    });
    
    socket.on('authenticated', () => {
      // Authentication confirmed
    });
    
    socket.on('reconnect', () => {
      // WebSocket reconnected
    });
    
    socket.on('reconnect_attempt', () => {
      // WebSocket reconnection attempt
    });
    
    socket.on('reconnect_error', () => {
      // WebSocket reconnection error
    });
    
    socket.on('reconnect_failed', () => {
      // WebSocket reconnection failed
    });

    // Listen for omnichannel events
    socket.on('omnichannelEvent', (event: OmnichannelEvent) => {
      
      // Notify all omnichannel subscribers
      omnichannelSubscribersRef.current.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('[WebSocket] Error in omnichannel subscriber:', error);
        }
      });
    });

    // Listen for ticket events
    socket.on('ticketEvent', async ({ type, ticket, timestamp }) => {
      // Process incoming WebSocket ticket event

      // First check if we already have a similar notification to prevent duplicates
      const isDuplicate = notifications.some(n => {
        // For bulk updates, check by type and timestamp rather than ticket ID
        if (type === 'bulk_update') {
          const existingTime = new Date(n.createdAt).getTime();
          const newTime = new Date(timestamp || Date.now()).getTime();
          const timeDiff = Math.abs(existingTime - newTime);
          // If bulk notifications are within 30 seconds of each other, consider them duplicates
          return n.type === 'bulk_update' && timeDiff < 10000;
        }

        // Check if this is the same ticket and event type within a 10-second window
        if (n.ticket.id === ticket.id && n.type === type) {
          const existingTime = new Date(n.createdAt).getTime();
          const newTime = new Date(timestamp || Date.now()).getTime();
          const timeDiff = Math.abs(existingTime - newTime);
          // If notifications are within 10 seconds of each other, consider them duplicates
          const isDuplicateByTime = timeDiff < 10000;
          if (isDuplicateByTime) {

            return true;
          }
        }
        return false;
      });
      
      // Skip processing if this is a duplicate notification
      if (isDuplicate) {

        return;
      }
      
      let notification: Notification | null = null;
      
      try {
        // For bulk updates, we don't need a specific ticket ID
        if(type === 'bulk_update') {
          notification = {
            id: `ws-bulk-${Date.now()}`,
            type,
            ticket: {
              id: 0, // Use 0 for bulk updates
              title: ticket.title || 'Bulk Update',
              status: ticket.status || 'updated',
              description: ticket.description || '',
            },
            read: false,
            createdAt: new Date(timestamp || Date.now()),
            message: ticket.notificationMessage || ticket.message || ticket.title || 'Bulk status update completed',
            fromWebSocket: true
          };
        } else {

          // Ensure the ticket has all required properties for NotificationTicket
          // Validate that ticket has an ID before proceeding
          if (!ticket || !ticket.id) {
            console.error('Received notification without valid ticket ID');
            return; // Skip processing this notification
          }
          
          const notificationTicket: NotificationTicket = {
            id: ticket.id,
            title: ticket.title || 'Unknown Ticket',
            status: ticket.status || 'unknown',
            description: ticket.description || '',
            priority: ticket.priority || 'normal',
            'customer.full_name': ticket['customer.full_name'] || '',
            is_public: ticket.is_public || false
          };
          
          // Use the message from the server if available, otherwise generate one
          let message = '';
          
          // Check for notification message in different possible fields
          if (ticket.notificationMessage) {
            // Use the explicit notification message if available

            message = ticket.notificationMessage;
          } else if (type === 'statusChange' && ticket.status) {
            // For status change notifications, create a descriptive message

            message = `Ticket status changed to ${ticket.status}: #${ticket.id}`;
          } else if (ticket.title && (ticket.title.includes('commented on') || ticket.title.includes('comment'))) {
            // For comment notifications, use the exact message from the server

            message = ticket.title;
          } else if (ticket.message) {
            // Use message field if available

            message = ticket.message;
          } else {
            // Fallback to default message generation

            // Generate a default message based on event type
            switch (type) {
              case 'new':
                message = `New Ticket #${ticket.id} created`;
                break;
              case 'update':
                message = `Ticket #${ticket.id} updated`;
                break;
              case 'statusChange':
                message = `Ticket status changed to ${ticket.status}: #${ticket.id}`;
                break;
              case 'comment':
                message = `New comment on ticket #${ticket.id}`;
                break;
              default:
                message = ticket.title || `Ticket #${ticket.id} updated`;
            }
          }
          
          // Create a unique ID that includes the ticket ID and event type to help with deduplication
          const uniqueId = `ws-${ticket.id}-${type}-${Date.now()}`;
          
        
          notification = {
            id: uniqueId,
            type,
            ticket: notificationTicket,
            read: false,
            createdAt: new Date(timestamp || Date.now()),
            message: message,
            fromWebSocket: true // Mark this as a WebSocket notification
          };
        }
      } catch (error) {

        return; // Exit early if there's an error
      }

      // Only proceed if we successfully created a notification with a valid ticket
      if (notification) {

        // Store the notification in localStorage to prevent it from disappearing
        try {
          // Get existing WebSocket notifications from localStorage
          const storedNotificationsStr = localStorage.getItem('webSocketNotifications') || '[]';
          const storedNotifications = JSON.parse(storedNotificationsStr);
          
          // Check for duplicates in localStorage before adding
          const isDuplicateInStorage = storedNotifications.some((n: any) => {
            if(notification.type === 'bulk_update') {
              // For bulk updates, check by type and timestamp rather than ticket ID
              if (n.type === notification.type ) {
                const existingTime = new Date(n.timestamp).getTime();
                const newTime = new Date().getTime();
                const timeDiff = Math.abs(existingTime - newTime);
                return timeDiff < 30000;
              }
            }
            if (n.ticketId === notification.ticket.id && n.type === notification.type) {
              const existingTime = new Date(n.timestamp).getTime();
              const newTime = new Date().getTime();
              const timeDiff = Math.abs(existingTime - newTime);
              // If notifications are within 10 seconds of each other, consider them duplicates
              return timeDiff < 10000;
            }
            return false;
          });
          
          if (isDuplicateInStorage) {
            console.log("Duplicate notification found");
            return; // Skip adding to localStorage
          }
          
          // Add the new notification
          const updatedNotifications = [
            {
              id: notification.id,
              type: notification.type,
              ticketId: notification.ticket.id,
              message: notification.message,
              timestamp: new Date().toISOString()
            },
            ...storedNotifications
          ];

          console.log("Updated notifications:", updatedNotifications);
          
          // Keep only the last 20 notifications to prevent localStorage from getting too large
          const trimmedNotifications = updatedNotifications.slice(0, 20);

          console.log("Trimmed notifications:", trimmedNotifications);
          
          // Save back to localStorage
          localStorage.setItem('webSocketNotifications', JSON.stringify(trimmedNotifications));

        } catch (storageError) {

        }
        
        // Update state with the new notification
        setNotifications(prev => {
          // Add new notification to existing ones, but first check if we already have it
          const exists = prev.some(n => 
            (typeof n.id === 'string' && n.id === notification.id) ||
            (n.ticket && notification.ticket && n.ticket.id === notification.ticket.id && n.message === notification.message && 
             Math.abs(new Date(n.createdAt).getTime() - new Date(notification.createdAt).getTime()) < 5000)
          );
          
          if (exists) {

            return prev;
          }
          

          return [notification, ...prev];
        });
        
        setUnreadCount(prev => {
          // Increment unread count
          return prev + 1;
        });
      }

      // Trigger a refresh of the tickets page
      // Trigger ticket refresh
      triggerRefresh();

      // Play sound if enabled
      if (soundEnabled) {
        // Play notification sound
        try {
          await playNotificationSound();
          // Sound played successfully
        } catch (error) {
          console.error('Error playing notification sound:', error);
        }
      }
      
      // Don't automatically refresh notifications from server after receiving a WebSocket notification
      // This was causing the notifications to disappear
      // fetchNotifications();
    });


    return () => {
      socket.close();
    };
  }, [user, soundEnabled, triggerRefresh]);

  const toggleSound = async () => {
    const newValue = !soundEnabled;
    if (newValue) {
      const initialized = await initializeAudio();
      if (!initialized) {
        // Failed to initialize audio
      }
    }
    setSoundEnabled(newValue);
    localStorage.setItem('notificationSound', String(newValue));
  };

  useEffect(() => {
    // Initialize audio if sound is enabled
    if (soundEnabled) {
      // Setup event listeners to initialize audio on user interaction
      setupAudioContextEvents();
      
      // Also try to initialize directly in case we already have user interaction
      initializeAudio().catch(_error => {
        // Failed to initialize audio
      });
    }
  }, [soundEnabled]); // Run when sound enabled state changes

  const subscribeToOmnichannel = useCallback((callback: (event: OmnichannelEvent) => void) => {
    omnichannelSubscribersRef.current.add(callback);
    
    // Return unsubscribe function
    return () => {
      omnichannelSubscribersRef.current.delete(callback);
    };
  }, []);

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    soundEnabled,
    toggleSound,
    connectionStatus,
    initializeAudio,
    loadMoreNotifications,
    hasMoreNotifications,
    subscribeToOmnichannel
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
